import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, X, FileText, ShieldCheck, Loader2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { api } from '../lib/api';
import { logger } from '../lib/logger';

const STORAGE_DISMISSED = 'gp_exit_intent_dismissed';
const STORAGE_LEAD_ID = 'gp_vsl_lead_id';
const STORAGE_PHASE1 = 'gp_vsl_phase1';
// Mobile fallback: scroll-up gesture from below this Y after this idle delay.
const MOBILE_IDLE_MS = 25000;

/**
 * Exit-intent popup — fires once per visitor when:
 *  - Desktop: cursor leaves the viewport from the top edge
 *  - Mobile: 25 s of idle scroll without form submission
 *
 * Offers an instant download of the 3 brochures + project financing in exchange
 * for a single email field. Auto-skips if the user has already submitted a lead.
 */
export default function ExitIntentPopup({ enabled = true }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const armedRef = useRef(true);

  // Skip entirely for users who already submitted any form (avoid double-asking).
  useEffect(() => {
    if (!enabled) return undefined;
    let dismissed = false;
    try {
      dismissed =
        localStorage.getItem(STORAGE_DISMISSED) === '1' ||
        localStorage.getItem(STORAGE_LEAD_ID) ||
        localStorage.getItem(STORAGE_PHASE1) === '1';
    } catch (err) {
      logger.debug('ExitIntentPopup: localStorage read failed', err);
    }
    if (dismissed) {
      armedRef.current = false;
      return undefined;
    }

    const fire = () => {
      if (!armedRef.current) return;
      armedRef.current = false;
      setOpen(true);
    };

    // Desktop: cursor leaving from top edge
    const onMouseOut = (e) => {
      if (e.clientY <= 0 && !e.relatedTarget) fire();
    };
    document.addEventListener('mouseout', onMouseOut);

    // Mobile fallback: idle timer
    let idleTimer = null;
    const isCoarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    if (isCoarse) {
      idleTimer = setTimeout(fire, MOBILE_IDLE_MS);
    }

    return () => {
      document.removeEventListener('mouseout', onMouseOut);
      if (idleTimer) clearTimeout(idleTimer);
    };
  }, [enabled]);

  const handleClose = () => {
    try { localStorage.setItem(STORAGE_DISMISSED, '1'); } catch (err) { logger.debug('ExitIntentPopup: localStorage dismiss save failed', err); }
    setOpen(false);
  };

  // Esc to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError('');
    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError('Inserisci un indirizzo email valido');
      return;
    }
    setSubmitting(true);
    try {
      const utm = {};
      try {
        const qs = new URLSearchParams(window.location.search);
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((k) => {
          const v = qs.get(k);
          if (v) utm[k] = v;
        });
      } catch (err) {
        logger.debug('ExitIntentPopup: UTM parsing failed', err);
      }
      const r = await api.post('/public/vsl-gate-lead', {
        first_name: 'Lead',
        last_name: 'Exit Intent',
        email: value,
        consent_privacy: true,
        page_context: 'exit_intent',
        utm: Object.keys(utm).length ? utm : undefined,
      });
      const leadId = r?.data?.lead_id || null;
      try {
        localStorage.setItem(STORAGE_PHASE1, '1');
        localStorage.setItem(STORAGE_DISMISSED, '1');
        if (leadId) localStorage.setItem(STORAGE_LEAD_ID, leadId);
      } catch (err) {
        logger.debug('ExitIntentPopup: localStorage post-submit save failed', err);
      }
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Errore, riprova tra poco';
      setError(typeof msg === 'string' ? msg : 'Errore, riprova tra poco');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[8500] flex items-center justify-center p-3 sm:p-6 md:p-10 bg-black/80 backdrop-blur-md animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-title"
      data-testid="exit-intent-popup"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="relative w-full max-w-lg bg-[var(--surface-1)] rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] overflow-hidden border border-[var(--gp-gold)]/30 animate-in zoom-in-95 duration-200">
        {/* Top gold strip */}
        <div className="h-1 w-full bg-gradient-to-r from-[var(--gp-gold-soft)] via-[var(--gp-gold)] to-[var(--gp-gold-soft)]" />

        {/* Close */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-3 right-3 inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-[var(--gp-forest)]/10 text-[var(--gp-forest)]/60 hover:text-[var(--gp-forest)] transition-colors"
          aria-label="Chiudi"
          data-testid="exit-intent-close"
        >
          <X className="w-4 h-4" />
        </button>

        {!done ? (
          <div className="p-6 md:p-8">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[var(--gp-gold)] font-semibold">
              <Sparkles className="w-3 h-3" />
              Aspetta — un&apos;ultima cosa
            </div>
            <h2
              id="exit-intent-title"
              className="font-serif text-2xl md:text-[2rem] leading-tight text-[var(--gp-forest)] mt-3"
            >
              Stai partendo senza le brochure?
            </h2>
            <p className="text-sm md:text-[15px] text-[var(--gp-forest)]/75 mt-2 md:mt-3 leading-relaxed">
              Lascia la tua email: <b className="text-[var(--gp-forest)]">ti mando subito</b> le 3 brochure ufficiali (Lombok · Spille · Chumphon) <b className="text-[var(--gp-forest)]">+ il project financing completo</b>. Le leggi con calma quando vuoi.
            </p>

            <ul className="mt-4 space-y-1.5 text-[13px] text-[var(--gp-forest)]/80">
              <li className="flex items-start gap-2">
                <FileText className="w-3.5 h-3.5 mt-1 text-[var(--gp-gold)] shrink-0" />
                <span>3 brochure ufficiali con render, layout, KPI</span>
              </li>
              <li className="flex items-start gap-2">
                <FileText className="w-3.5 h-3.5 mt-1 text-[var(--gp-gold)] shrink-0" />
                <span>Project financing trasparente con scenari indicativi</span>
              </li>
              <li className="flex items-start gap-2">
                <ShieldCheck className="w-3.5 h-3.5 mt-1 text-[var(--gp-gold)] shrink-0" />
                <span>Niente call di vendita finché non vuoi tu</span>
              </li>
            </ul>

            <form onSubmit={handleSubmit} className="mt-5 md:mt-6 space-y-3">
              <Input
                type="email"
                placeholder="La tua email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                className="h-12 text-base"
                data-testid="exit-intent-email"
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                enterKeyHint="send"
                name="email"
              />
              {error && (
                <div className="text-xs text-red-600" data-testid="exit-intent-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 text-sm md:text-base tracking-[0.05em] font-semibold uppercase shadow-lg"
                data-testid="exit-intent-submit"
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Invio in corso…
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    Mandami le brochure <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
              <p className="text-[10px] md:text-[11px] text-[var(--gp-forest)]/55 text-center leading-relaxed pt-1">
                <ShieldCheck className="w-3 h-3 inline mr-1 -mt-0.5" />
                GDPR · Niente spam · Disiscrivi con un click in fondo a ogni email
              </p>
            </form>
          </div>
        ) : (
          <div className="p-6 md:p-8 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[var(--gp-gold)]/15 text-[var(--gp-gold)] mx-auto">
              <FileText className="w-7 h-7" />
            </div>
            <h2 className="font-serif text-2xl md:text-3xl text-[var(--gp-forest)] mt-4 leading-tight">
              Le brochure sono in arrivo.
            </h2>
            <p className="text-sm text-[var(--gp-forest)]/75 mt-2 leading-relaxed">
              Controlla la tua inbox tra qualche secondo. Se non vedi nulla, guarda anche in <b>spam / promozioni</b>.
            </p>
            <Button
              onClick={handleClose}
              className="mt-5 h-11 px-6 text-sm font-semibold tracking-[0.05em] uppercase"
              data-testid="exit-intent-done"
            >
              Continuo a navigare
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
